import { Injectable, NgZone, inject, Optional, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from 'src/environments/environment';
import { ZERO_ADDRESS } from '@/constants/utils';

import { StateService } from './state.service';
import { DataService } from './data.service';
import { GraphQLQueriesService } from './graphql-queries.service';
import { Apollo } from 'apollo-angular';
import { DelegateXYZService } from './delegate-xyz.service';
import { AuthService } from './auth.service';

import { Observable, BehaviorSubject, catchError, filter, firstValueFrom, of, tap, interval, from, mergeMap, toArray, lastValueFrom, switchMap, forkJoin, map, race, defaultIfEmpty, take, concatMap } from 'rxjs';

import CryptoPunksMarketABI from '../../abi/CryptoPunksMarket.json';
import CryptoPhunksMarketABI from '../../abi/CryptoPhunksMarket.json';
import CryptoPhunksAuctionHouseABI from '../../abi/CryptoPhunksAuctionHouse.json';
import PunkV1Wrapper from '../../abi/CryptoPunksV1WrappedToken.json';
import UnwrapV1PunksABI from '../../abi/UnwrapV1Punks.json';
import ERC721ABI from '../../abi/ERC721.json';
import punkDataABI from '../../abi/PunkData.json';
import CigABI from '../../abi/Cig.json';
import CigAirdropABI from '../../abi/CigAirdrop.json';
import FlashLoanContractABI from '../../abi/FlashLoanContract.json';
import FlashLoanContractPhunkABI from '../../abi/FlashLoanContractPhunk.json';
import StashFactoryABI from '../../abi/StashFactory.json';

import { WeiToEthPipe } from '@/pipes/wei-to-eth.pipe';
import { GlobalState } from '@/models/auction-house/state';
import { PlatformService } from './platform.service';

import { Chain, mainnet, sepolia, anvil } from 'viem/chains';
import { Config, GetAccountReturnType, disconnect, getAccount, getPublicClient, getWalletClient, http, readContract, reconnect, switchChain, watchAccount, waitForTransactionReceipt, getEnsName, getEnsResolver, getEnsText } from '@wagmi/core';
import { createAppKit, type AppKit } from '@reown/appkit';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { TransactionReceipt, decodeFunctionData, formatEther, formatUnits, isAddress, parseEther } from 'viem';
import { ENSMetadata } from '@/models/ens';
import { CigState, CigStateDto } from '@/models/cig';
import { createPunkBidTypedData, createERC721BidTypedData, PunkBidOrderInput, PunkBidTypedData, ERC721BidTypedData } from '@/utils/punk-bid-typed-data';
import { isLocalDevMimicEnabled } from '@/utils/local-mimic';

const marketAddress = environment.punksMarketAddress;
const dataAddress = environment.punkDataAddress;
const cigAddress = environment.cigAddress;
// const cigAirdropAddress = '0x8f330f4f558C2B15dB1A4ab9AA028d5ca01bB783';
const cigAirdropAddress = '0x417788c2B1482F003B4f2FbB8ab21eF5587002a8';
const stashFactoryAddress = environment.stashFactoryAddress;
const alchemyApiKey = environment.alchemyApiKey;

// Partial Stash ABI (functions we call + error definitions for readable reverts)
const StashABI = [
  { "type": "error", "name": "BidExpired", "inputs": [] },
  { "type": "error", "name": "InvalidBid", "inputs": [] },
  { "type": "error", "name": "BidCanceled", "inputs": [] },
  { "type": "error", "name": "Unauthorized", "inputs": [] },
  { "type": "error", "name": "OrderNotFound", "inputs": [] },
  { "type": "error", "name": "InvalidProof", "inputs": [] },
  { "type": "error", "name": "TooManyOrders", "inputs": [] },
  { "type": "error", "name": "UnknownOrderType", "inputs": [] },
  { "type": "error", "name": "FailedToBuyPunk", "inputs": [] },
  { "type": "error", "name": "NoBidForAuction", "inputs": [] },
  { "type": "error", "name": "InvalidSignature", "inputs": [] },
  { "type": "error", "name": "CallerNotAuction", "inputs": [] },
  { "type": "error", "name": "FailedToWithdraw", "inputs": [] },
  { "type": "error", "name": "AlreadyInitialized", "inputs": [] },
  { "type": "error", "name": "InvalidOrderAlteration", "inputs": [] },
  { "type": "error", "name": "RequestExceedsAvailableBalance", "inputs": [] },
  { "type": "error", "name": "CannotTransferMoreThanBidAmount", "inputs": [] },
  {
    "type": "function",
    "name": "processPunkBid",
    "inputs": [
      {
        "components": [
          {
            "components": [
              {
                "name": "numberOfUnits",
                "type": "uint16",
                "internalType": "uint16"
              },
              {
                "name": "pricePerUnit",
                "type": "uint80",
                "internalType": "uint80"
              },
              {
                "name": "auction",
                "type": "address",
                "internalType": "address"
              }
            ],
            "name": "order",
            "type": "tuple",
            "internalType": "struct Order"
          },
          {
            "name": "accountNonce",
            "type": "uint256",
            "internalType": "uint256"
          },
          {
            "name": "bidNonce",
            "type": "uint256",
            "internalType": "uint256"
          },
          {
            "name": "expiration",
            "type": "uint256",
            "internalType": "uint256"
          },
          {
            "name": "root",
            "type": "bytes32",
            "internalType": "bytes32"
          }
        ],
        "name": "bid",
        "type": "tuple",
        "internalType": "struct PunkBid"
      },
      {
        "name": "punkIndex",
        "type": "uint256",
        "internalType": "uint256"
      },
      {
        "name": "signature",
        "type": "bytes",
        "internalType": "bytes"
      },
      {
        "name": "proof",
        "type": "bytes32[]",
        "internalType": "bytes32[]"
      }
    ],
    "outputs": [],
    "stateMutability": "nonpayable"
  },
  {
    "type": "function",
    "name": "availableLiquidityWETHAndETH",
    "inputs": [],
    "outputs": [
      {
        "name": "",
        "type": "uint256",
        "internalType": "uint256"
      }
    ],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "availableLiquidity",
    "inputs": [
      {
        "name": "tokenAddress",
        "type": "address",
        "internalType": "address"
      }
    ],
    "outputs": [
      {
        "name": "",
        "type": "uint256",
        "internalType": "uint256"
      }
    ],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "punkAccountNonce",
    "inputs": [],
    "outputs": [
      {
        "name": "",
        "type": "uint56",
        "internalType": "uint56"
      }
    ],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "withdraw",
    "inputs": [
      {
        "name": "tokenAddress",
        "type": "address",
        "internalType": "address"
      },
      {
        "name": "amount",
        "type": "uint256",
        "internalType": "uint256"
      }
    ],
    "outputs": [],
    "stateMutability": "nonpayable"
  },
  {
    "type": "function",
    "name": "cancelPunkBid",
    "inputs": [
      {
        "name": "bidNonce",
        "type": "uint256",
        "internalType": "uint256"
      }
    ],
    "outputs": [],
    "stateMutability": "nonpayable"
  },
  {
    "type": "function",
    "name": "cancelAllPunkBids",
    "inputs": [],
    "outputs": [],
    "stateMutability": "nonpayable"
  },
  {
    "type": "function",
    "name": "cancelAllERC721Bids",
    "inputs": [],
    "outputs": [],
    "stateMutability": "nonpayable"
  },
  {
    "type": "function",
    "name": "cancelERC721Bid",
    "inputs": [
      {
        "name": "bidNonce",
        "type": "uint256",
        "internalType": "uint256"
      }
    ],
    "outputs": [],
    "stateMutability": "nonpayable"
  },
  {
    "type": "function",
    "name": "processERC721Bid",
    "inputs": [
      {
        "components": [
          {
            "components": [
              {
                "name": "numberOfUnits",
                "type": "uint16",
                "internalType": "uint16"
              },
              {
                "name": "pricePerUnit",
                "type": "uint80",
                "internalType": "uint80"
              },
              {
                "name": "auction",
                "type": "address",
                "internalType": "address"
              }
            ],
            "name": "order",
            "type": "tuple",
            "internalType": "struct Order"
          },
          {
            "name": "accountNonce",
            "type": "uint256",
            "internalType": "uint256"
          },
          {
            "name": "bidNonce",
            "type": "uint256",
            "internalType": "uint256"
          },
          {
            "name": "expiration",
            "type": "uint256",
            "internalType": "uint256"
          },
          {
            "name": "root",
            "type": "bytes32",
            "internalType": "bytes32"
          }
        ],
        "name": "bid",
        "type": "tuple",
        "internalType": "struct ERC721Bid"
      },
      {
        "name": "tokenId",
        "type": "uint256",
        "internalType": "uint256"
      },
      {
        "name": "signature",
        "type": "bytes",
        "internalType": "bytes"
      },
      {
        "name": "proof",
        "type": "bytes32[]",
        "internalType": "bytes32[]"
      }
    ],
    "outputs": [],
    "stateMutability": "nonpayable"
  },
  {
    "type": "function",
    "name": "erc721AccountNonce",
    "inputs": [],
    "outputs": [
      {
        "name": "",
        "type": "uint56",
        "internalType": "uint56"
      }
    ],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "version",
    "inputs": [],
    "outputs": [
      {
        "name": "",
        "type": "uint256",
        "internalType": "uint256"
      }
    ],
    "stateMutability": "view"
  }
] as const;

const projectId = '9455b1a68e7f81eee6e1090c12edbf00';

/** WalletConnect / Reown require metadata.url to match the page origin (critical on IPFS gateways). */
function getWalletConnectMetadata() {
  const canonicalBase = environment.marketplaceUrl?.replace(/\/$/, '') ?? '';
  const pageOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin.replace(/\/$/, '')
      : canonicalBase;
  const iconBase = canonicalBase || pageOrigin;

  return {
    name: environment.marketplaceName || environment.collectionTitle || 'CryptoPunks Marketplace',
    description: `${environment.collectionTitle} Marketplace`,
    url: pageOrigin || canonicalBase,
    icons: iconBase ? [`${iconBase}/favicon.ico`] : [],
  };
}

const chains: readonly [Chain, ...Chain[]] = environment.production
  ? [mainnet]
  : [sepolia, anvil];

export interface SignPunkBidParams {
  order: PunkBidOrderInput;
  root: `0x${string}`;
  accountNonce: bigint | number | string;
  bidNonce: bigint | number | string;
  expiration: bigint | number | string;
  verifyingContract?: `0x${string}`;
}

@Injectable({
  providedIn: 'root'
})

export class Web3Service {
  private readonly MOCKED_LEGACY_WITHDRAWAL_ETH = 0.01;

  config!: Config;
  modal!: AppKit;
  connectors: any[] = [];

  web3Connecting: boolean = false;

  private gasPriceSubject = new BehaviorSubject<string>('0');
  public gasPrice$ = this.gasPriceSubject.asObservable();

  private currentBlockSubject = new BehaviorSubject<number>(0);
  public currentBlock$ = this.currentBlockSubject.asObservable();

  private lastIndexedBlockSubject = new BehaviorSubject<number>(0);
  public lastIndexedBlock$ = this.lastIndexedBlockSubject.asObservable();

  // Approval modal state
  private approvalModalActiveSubject = new BehaviorSubject<boolean>(false);
  public approvalModalActive$ = this.approvalModalActiveSubject.asObservable();

  // Signature explanation modal state
  private signatureExplanationModalActiveSubject = new BehaviorSubject<boolean>(false);
  public signatureExplanationModalActive$ = this.signatureExplanationModalActiveSubject.asObservable();
  private pendingAuthAddress: string | null = null;
  private authInFlight = false;

  // Wallet mimic: saved JWT state for restoring after deactivation
  private savedJwtBeforeMimic: { token: string; expiry: number; address: string | null } | null = null;
  /** True when localhost mimic turned on web3Connected without a real wallet. */
  private localMimicSyntheticConnection = false;
  private mimicStateChangedSubject = new BehaviorSubject<number>(0);
  public mimicStateChanged$ = this.mimicStateChangedSubject.asObservable();

  // Inject services conditionally for SSR
  private platformService = inject(PlatformService);
  private dataSvc = this.platformService.isClient ? inject(DataService, { optional: true }) : null;
  private graphQLQueriesSvc = this.platformService.isClient ? inject(GraphQLQueriesService, { optional: true }) : null;
  private apollo = this.platformService.isClient ? inject(Apollo, { optional: true }) : null;
  private delegateSvc = this.platformService.isClient ? inject(DelegateXYZService, { optional: true }) : null;
  private http = this.platformService.isClient ? inject(HttpClient, { optional: true }) : null;
  private authService = this.platformService.isClient ? inject(AuthService, { optional: true }) : null;

  constructor(
    private stateSvc: StateService,
    private zone: NgZone,
    private weiToEthPipe: WeiToEthPipe,
    @Optional() @Inject('WAGMI_CONFIG') private injectedConfig?: any
  ) {
    // Use injected config if available (SSR), otherwise create client-side config
    if (this.injectedConfig) {
      this.config = this.injectedConfig;
      console.log('Web3Service: Using injected Wagmi config for SSR');
    } else if (this.platformService.isClient) {
      if (environment.mocked) {
        // Keep a visible legacy withdrawal amount in mocked mode even without wallet connection.
        this.stateSvc.setHasWithdrawal(this.MOCKED_LEGACY_WITHDRAWAL_ETH);
      }

      const wagmiAdapter = new WagmiAdapter({
        networks: chains as any,
        projectId,
        transports: {
          [mainnet.id]: http(environment.rpcHttpProvider),
          [sepolia.id]: http('https://eth-sepolia.g.alchemy.com/v2/yEf70s_LbwNJ1rXVo9Z668hLS7NlFPfv'),
          [anvil.id]: http('http://localhost:8545'),
        }
      });

      this.config = wagmiAdapter.wagmiConfig;

      this.modal = createAppKit({
        adapters: [wagmiAdapter],
        networks: chains as any,
        metadata: getWalletConnectMetadata(),
        projectId,
        featuredWalletIds: [
          'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // WalletConnect
        ],
        features: {
          analytics: false,
        },
      });

      this.dataSvc?.ownedData$.subscribe((res) => {
        this.checkListedTokensApprovals();
      });


      // Start global reconnect check lifecycle
      this.stateSvc.setReconnecting(true);
      this.stateSvc.setCheckedReconnection(false);

      this.createListeners();
      reconnect(this.config)
        .then(() => console.log('reconnected'))
        .finally(() => {
          // If no account change occurs, still mark reconnection as checked
          this.stateSvc.setReconnecting(false);
          setTimeout(() => this.stateSvc.setCheckedReconnection(true), 600);
        });

      this.startBlockTracking();
    }
  }

  createListeners(): void {
    // Don't create listeners on server
    if (this.platformService.isServer) {
      return;
    }

    const connectedState = new Observable((observer) => watchAccount(
      this.config,
      {
        onChange(data) {
          observer.next(data);
        }
      }
    ));

    connectedState.pipe(
      tap((account: any) => {
        // First account event means reconnection has been checked
        this.stateSvc.setReconnecting(false);
        setTimeout(() => this.stateSvc.setCheckedReconnection(true), 600);
        if (account.isDisconnected && !this.shouldPreserveLocalMimicSession()) {
          void this.disconnectWeb3();
        }
      }),
      filter((account) => account.isConnected),
      tap((account) => this.connectWeb3(account.address as string)),
      catchError((err) => {
        this.disconnectWeb3();
        return of(err);
      }),
    ).subscribe();
  }

  async getBalance(address:string) {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) return '0';
    const balance = await publicClient.getBalance({ address: address as `0x${string}` });
    const eth = parseFloat(formatEther(balance)).toFixed(3);
    return eth;
  }  

  public getPublicClient() {
    console.log('[Web3Service] getPublicClient called');
    console.log('[Web3Service] config exists:', !!this.config);
    console.log('[Web3Service] config type:', typeof this.config);
    
    // Utiliser le même pattern que les autres méthodes
    const result = getPublicClient(this.config);
    console.log('[Web3Service] getPublicClient result:', result);
    console.log('[Web3Service] getPublicClient result type:', typeof result);
    return result;
  }

  public async getWalletClient() {
    this.assertNotMimicking();
    console.log('[Web3Service] getWalletClient called');
    console.log('[Web3Service] config exists:', !!this.config);
    console.log('[Web3Service] config type:', typeof this.config);
    
    const result = await getWalletClient(this.config);
    console.log('[Web3Service] getWalletClient result:', result);
    console.log('[Web3Service] getWalletClient result type:', typeof result);
    return result;
  }

  getENSKeys(key: keyof ENSMetadata): string[] {
    switch (key) {
      case 'x': return ['com.twitter', 'twitter'];
      case 'discord': return ['com.discord', 'discord'];
      case 'telegram': return ['com.telegram', 'org.telegram', 'telegram'];
      case 'github': return ['com.github', 'github'];
      default: return [key];
    }
  }  

  getENSMetadata(address: string): Observable<ENSMetadata | null> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) return of(null);

    return from(getEnsName(this.config, { address: address as `0x${string}` })).pipe(
      tap(resolvedENS => console.log('resolvedENS', resolvedENS)),
      switchMap(resolvedENS => {
        if (!resolvedENS) return of(null);
  
        return from(getEnsResolver(this.config, { name: resolvedENS })).pipe(
          switchMap(resolver => {
            if (!resolver) return of(null);
  
            const keys: (keyof ENSMetadata)[] = ['description', 'discord', 'telegram','x', 'github', 'url'];
  
            const observables = keys.map(key =>
              from(this.getENSKeys(key)).pipe(
                concatMap(altKey =>
                  from(getEnsText(this.config, { name: resolvedENS, key: altKey })).pipe(
                    catchError(() => of(null)),
                    filter(value => value !== null && value.trim() !== '')
                  )
                ),
                take(1), 
                defaultIfEmpty(null) 
              )
            );
            
  
            return forkJoin(observables).pipe(
              map(([description, discord, telegram, x, github, url]) => { 
                return {
                  description, discord, telegram, x, github, url, ens: resolvedENS ?? address,
                  followingCount: 0, followerCount: 0
                }
              })
            );
          })
        );
      }),
      catchError((err) => {
        console.error(err)
        return of(null);
      }),
    );
  }

  async getNftBalance(contract:string, wallet:string) {
    console.log('getNftBalance', contract, wallet);
    const publicClient = getPublicClient(this.config);
    if (!publicClient) return -1;
    
    const balance = await publicClient.readContract({
      address: contract as `0x${string}`,
      abi: ERC721ABI,
      functionName: 'balanceOf',
      args: [wallet as `0x${string}`],
    });
    
    return Number(balance);
  }

  async getERC20Balance(contract: string, wallet: string, decimals: number = 18): Promise<string> {
    console.log('getERC20Balance', contract, wallet, decimals);
    const publicClient = getPublicClient(this.config);
    if (!publicClient) return '0';
    
    try {
      const balance = await publicClient.readContract({
        address: contract as `0x${string}`,
        abi: [
          {
            "constant": true,
            "inputs": [{"name": "_owner", "type": "address"}],
            "name": "balanceOf",
            "outputs": [{"name": "balance", "type": "uint256"}],
            "type": "function"
          }
        ],
        functionName: 'balanceOf',
        args: [wallet as `0x${string}`],
      });
      
      const formattedBalance = formatUnits(balance as bigint, decimals);
      return parseFloat(formattedBalance).toFixed(6);
    } catch (error) {
      console.error('Error getting ERC20 balance:', error);
      return '0';
    }
  }

  async connect(): Promise<void> {
    if (this.platformService.isServer) {
      return;
    }
    
    console.log('Connecting to Web3');
    try {
      await this.modal.open();
    } catch (error) {
      console.log(error);
      this.disconnectWeb3();
    }
  }

  async connectWeb3(address: string): Promise<void> {
    if (environment.mocked) {
      // await this.connectWeb3('0x696ed7b26f4b019ceec78dc8b9140ad64a6f354b') // bnfts
      // address = '0x696ed7b26f4b019ceec78dc8b9140ad64a6f354b' // bnfts
      // address = '0x1e593f141c90f8638ed657634cc62f5bdcf9cfeb' // cryptopunks.eth
      // address = '0xf45e3c257a11d3ab7341a401ea59c503250b4a95' // bnfts
      // await this.connectWeb3('0xf45e3c257a11d3ab7341a401ea59c503250b4a95')
      // await this.connectWeb3('0xc49784aeb7da812212cad9cf65ad2ccd8ac5b850') // 
    }
    if (this.platformService.isServer) {
      return;
    }

    if (!address) return;

    address = address.toLowerCase();

    // Deactivate mimic only when switching to a different real wallet (not on every reconnect)
    if (this.stateSvc.isMimicking()) {
      const previousReal = this.stateSvc.getRealWalletAddress();
      if (
        previousReal != null &&
        previousReal.toLowerCase() !== address.toLowerCase()
      ) {
        this.deactivateMimic();
      }
    }

    if (!this.shouldPreserveLocalMimicSession()) {
      this.localMimicSyntheticConnection = false;
    }
    this.stateSvc.setWeb3Connected(true);
    this.setWalletAddress(address);
    this.checkHasWithdrawal(address);
    this.getCigBalance(address);
    this.loadDelegations(address);
    
    // Check if we already have a valid token.
    // If not, show signature explanation modal before requesting signature.
    // This is required for any feature that relies on authenticated identity
    // (e.g. stash bids and Telegram notification linking).
    const requiresAuthenticatedIdentity =
      environment.featureFlags?.STASH_BID_SUPPORT === true ||
      environment.featureFlags?.NEW_NOTIFICATION_SYSTEM === true;

    if (!this.stateSvc.isTokenValid() && requiresAuthenticatedIdentity) {
      const modalAlreadyOpen = this.signatureExplanationModalActiveSubject.getValue();
      const samePendingAddress = this.pendingAuthAddress === address;

      // watchAccount can emit multiple connected events; avoid duplicate signature flows.
      if (!this.authInFlight && !(modalAlreadyOpen && samePendingAddress)) {
        this.pendingAuthAddress = address;
        this.showSignatureExplanationModal();
      }
    } else {
      // Token is valid, no need to show modal or request signature
      console.log('[Web3Service] JWT token already valid, skipping signature request');
    }
  }

  async disconnectWeb3(): Promise<void> {
    if (this.platformService.isServer) {
      return;
    }

    const preserveLocalMimic = this.shouldPreserveLocalMimicSession();

    if (getAccount(this.config)) {
      if (this.stateSvc.isMimicking() && !preserveLocalMimic) {
        this.stateSvc.clearMimicAddress();
        this.savedJwtBeforeMimic = null;
      }

      disconnect(this.config);

      if (!preserveLocalMimic) {
        this.stateSvc.setWeb3Connected(false);
        this.stateSvc.setWalletAddress(null);
        this.stateSvc.setCigBalances(null);
        this.stateSvc.setOutgoingDelegations([]);
        this.stateSvc.setIncomingDelegations([]);
        this.setWalletAddress(null);
        this.stateSvc.clearJwtToken();
      }
    }
  }

  private assertNotMimicking(): void {
    if (this.stateSvc.isMimicking()) {
      throw new Error('Write operations are blocked while mimicking a wallet. Deactivate mimic mode first.');
    }
  }

  async checkNetwork(): Promise<GetAccountReturnType> {
    this.assertNotMimicking();
    switchChain(this.config, {
      chainId: environment.chainId
    });
    return getAccount(this.config);
  }

  async checkHasWithdrawal(address: string): Promise<void> {
    if (environment.mocked) {
      this.stateSvc.setHasWithdrawal(this.MOCKED_LEGACY_WITHDRAWAL_ETH);
      return;
    }

    // no global withdraw for v1 contract
    if (environment.collection === 'cryptopunks-v1') return;
    const publicClient = getPublicClient(this.config);
    const pendingWithdrawals = await publicClient?.readContract({
      address: marketAddress as `0x${string}`,
      abi: environment.collection === 'cryptophunks' ? CryptoPhunksMarketABI : CryptoPunksMarketABI,
      functionName: 'pendingWithdrawals',
      args: [address],
    });

    const weiValue = pendingWithdrawals?.toString() as string;
    const ethValue = this.weiToEthPipe.transform(weiValue);
    this.stateSvc.setHasWithdrawal(ethValue);
  }

  setWalletAddress(address: any) {
    this.stateSvc.setWalletAddress(address);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // CIG ///////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  async cigContractInteraction(functionName: string, args: any[], value?: string): Promise<string | undefined> {
    if (!functionName) return;

    const network = await this.checkNetwork();
    const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });

    const tx: any = {
      address: cigAddress as `0x${string}`,
      abi: CigABI,
      functionName,
      args
    };
    if (value) tx.value = value;

    return await walletClient?.writeContract(tx);
  }

  async claimCig(punkId: string): Promise<string | undefined> {
    return await this.cigContractInteraction('claim', [punkId]);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // CIG AIRDROP (previewClaim / claim with punksV2TokenIds) ///////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  async cigAirdropPreviewClaim(userAddress: string, tokenIds: number[]): Promise<bigint[][] | undefined> {
    try {
      const address = userAddress || (getAccount(this.config).address as string);
      if (!address) return undefined;
      const publicClient = getPublicClient(this.config);
      if (!publicClient) return undefined;

      // Build token arrays based on collection
      const isPhunks = environment.collection === 'cryptophunks';
      const isPunksV1 = environment.collection === 'cryptopunks-v1';
      const phunkTokenIds: number[] = isPhunks ? tokenIds : [];
      const punksV2TokenIds: number[] = (!isPhunks && !isPunksV1) ? tokenIds : [];
      const punksV1TokenIds: number[] = isPunksV1 ? tokenIds : [];

      const res: any = await publicClient.readContract({
        address: cigAirdropAddress as `0x${string}`,
        abi: CigAirdropABI as any,
        functionName: 'previewClaim',
        args: [
          0,
          address,
          phunkTokenIds,
          punksV2TokenIds,
          punksV1TokenIds
        ]
      });

      return res as bigint[][];
    } catch (error) {
      console.error('cigAirdropPreviewClaim error', error);
      return undefined;
    }
  }

  async cigAirdropClaim(tokenIds: number[]): Promise<string | undefined> {
    try {
      const network = await this.checkNetwork();
      const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
      // Build token arrays based on collection
      const isPhunks = environment.collection === 'cryptophunks';
      const isPunksV1 = environment.collection === 'cryptopunks-v1';
      const phunkTokenIds: number[] = isPhunks ? tokenIds : [];
      const punksV2TokenIds: number[] = (!isPhunks && !isPunksV1) ? tokenIds : [];
      const punksV1TokenIds: number[] = isPunksV1 ? tokenIds : [];
      const txHash = await walletClient?.writeContract({
        address: cigAirdropAddress as `0x${string}`,
        abi: CigAirdropABI as any,
        functionName: 'claim',
        args: [
          0,
          phunkTokenIds,
          punksV2TokenIds,
          punksV1TokenIds
        ]
      });
      return txHash;
    } catch (error) {
      console.error('cigAirdropClaim error', error);
      return undefined;
    }
  }

  async getCigBalance(address?: string): Promise<any> {
    if (!address) address = getAccount(this.config).address as string;
    if (!address) return;

    try {
      const [cigBalanceOf, cigClaimable, cigPrice] = await Promise.all([
        this.cigBalanceOf(address),
        this.cigClaimable(address),
        firstValueFrom(this.stateSvc.cigBalances$),
      ]);

      this.stateSvc.setCigBalances({
        balance: Number(formatUnits(cigBalanceOf, 18)),
        claimable: Number(formatUnits(cigClaimable, 18)),
        cigPrice: cigPrice?.cigPrice || 0,
      });
    } catch (error) {
      console.log(error);
    }
  }

  async loadDelegations(address: string): Promise<void> {
    if (!this.delegateSvc) {
      console.warn('DelegateXYZService not available');
      return;
    }

    try {
      console.log('Loading delegations for address:', address);

      // Load outgoing and incoming delegations in parallel
      const [outgoingDelegations, incomingDelegations] = await Promise.all([
        this.delegateSvc.getOutgoingDelegations(address),
        this.delegateSvc.getIncomingDelegations(address)
      ]);

      // Store delegations in state
      this.stateSvc.setOutgoingDelegations(outgoingDelegations);
      this.stateSvc.setIncomingDelegations(incomingDelegations);

      console.log(`Loaded ${outgoingDelegations.length} outgoing and ${incomingDelegations.length} incoming delegations`);
    } catch (error) {
      console.error('Error loading delegations:', error);
      // Reset delegations on error
      this.stateSvc.setOutgoingDelegations([]);
      this.stateSvc.setIncomingDelegations([]);
    }
  }

  async cigBalanceOf(address: string): Promise<any> {
    return await readContract(this.config, {
      address: cigAddress as `0x${string}`,
      abi: CigABI,
      functionName: 'balanceOf',
      args: [address],
    });
  }



  async cigClaimable(address: string): Promise<any> {
    return await readContract(this.config, {
      address: cigAddress as `0x${string}`,
      abi: CigABI,
      functionName: 'pendingCig',
      args: [address],
    });
  }

  async getCigStats(address: string): Promise<CigState | null> {
    try {
      const res = await readContract(this.config, {
        address: cigAddress as `0x${string}`,
        abi: CigABI,
        functionName: 'getStats',
        args: [address]
      }) as unknown as [bigint[], string, string, bigint[]];

      if (!res) return null;

      const [values, ceo, graffiti, reserves] = res;
      return {
        values: values || [],
        ceoAddress: ceo || '0x0000000000000000000000000000000000000000',
        graffiti: graffiti || '0x',
        reserves: reserves || []
      } satisfies CigState;
    } catch (e) {
      console.error('getCigStats error', e);
      return null;
    }
  }

  async getCigStatsDto(address: string): Promise<CigStateDto | null> {
    const raw = await this.getCigStats(address);
    if (!raw) return null;

    const values = raw.values || [];
    const ceoTokenId = values.length > 5 ? Number(values[5]) : null;
    const takeOverPrice = values.length > 4 ? values[4] : null;
    const takePrepaid = values.length > 1 ? values[1] : null;

    // decode bytes32 to string, trimming trailing nulls
    const graffiti = this.hexToStringSafe(raw.graffiti);

    return {
      ceoTokenId,
      takeOverPrice,
      takePrepaid,
      graffiti,
      ceoWallet: raw.ceoAddress || null
    } as CigStateDto;
  }

  private hexToStringSafe(hex: string): string {
    try {
      if (!hex || hex === '0x') return '';
      const bytes = hex.slice(2);
      let result = '';
      for (let i = 0; i < bytes.length; i += 2) {
        const byte = parseInt(bytes.substring(i, i + 2), 16);
        if (byte === 0) continue;
        result += String.fromCharCode(byte);
      }
      return result;
    } catch {
      return '';
    }
  }

  async sendCig(toAddress: string, weiValue: string): Promise<string | undefined> {
    return await this.cigContractInteraction('transfer', [toAddress, weiValue]);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // CONTRACT METHODS //////////////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  async punkContractInteraction(
    functionName: string, 
    args: any[], 
    value?: string,
    contractAddress?: string
  ): Promise<string | undefined> {
    if (!functionName) return;

    const network = await this.checkNetwork();
    const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });

    const tx: any = {
      address: (contractAddress || marketAddress) as `0x${string}`,
      abi: environment.collection === 'cryptophunks' ? CryptoPhunksMarketABI : CryptoPunksMarketABI,
      functionName,
      args
    };
    if (value) tx.value = value;

    return await walletClient?.writeContract(tx);
  }

  async erc721ContractInteraction(address:string, functionName: string, args: any[], value?: string): Promise<string | undefined> {
    return this.anyContractInteraction(address, functionName, args, ERC721ABI, value)
  }

  async anyContractInteraction(address:string, functionName: string, args: any[], abi:any, value?: string): Promise<string | undefined> {
    if (!functionName) return;

    const network = await this.checkNetwork();
    const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });

    const tx: any = {
      address,
      abi,
      functionName,
      args
    };
    if (value) tx.value = value;

    return await walletClient?.writeContract(tx);
  }


  async wrapperContractInteraction(functionName: string, args: any[], value?: string): Promise<string | undefined> {
    if (!functionName) return;

    const network = await this.checkNetwork();
    const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });

    const tx: any = {
      address: environment.wrappedPunksAddress,
      abi: PunkV1Wrapper,
      functionName,
      args
    };
    if (value) tx.value = value;

    return await walletClient?.writeContract(tx);
  }

  async cancelBidWithFlashLoan(punkIndex: string, bidAmount: string): Promise<string | undefined> {
    console.log('=== cancelBidWithFlashLoan START ===');
    console.log('punkIndex:', punkIndex);
    console.log('bidAmount:', bidAmount);
    
    if (!punkIndex || !bidAmount) {
      console.error('Missing required parameters');
      return;
    }

    try {
      const network = await this.checkNetwork();
      console.log('Network:', network);
      console.log('Chain ID:', network.chain?.id);
      
      const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
      console.log('Wallet client obtained:', !!walletClient);
      
      if (!walletClient) {
        console.error('Failed to get wallet client');
        return;
      }

      // Convert parameters to BigInt
      const punkIndexBigInt = BigInt(punkIndex);
      const bidAmountBigInt = BigInt(bidAmount);
      
      // Add 1 wei to the bid amount as required by the contract
      const bidAmountWithWei = bidAmountBigInt + BigInt(1);
      console.log('Original punkIndex:', punkIndex);
      console.log('Original bidAmount:', bidAmount);
      console.log('punkIndex as BigInt:', punkIndexBigInt.toString());
      console.log('bidAmount as BigInt:', bidAmountBigInt.toString());
      console.log('Bid amount with 1 wei:', bidAmountWithWei.toString());
      
      const tx: any = {
        address: environment.flashLoanContractAddress,
        abi: environment.collection === 'cryptophunks' ? FlashLoanContractPhunkABI : FlashLoanContractABI,
        functionName: 'initiateFlashLoan',
        args: [[punkIndexBigInt], [bidAmountWithWei]]
      };
      
      console.log('Transaction object:', tx);
      console.log('Flash loan contract address:', environment.flashLoanContractAddress);
      console.log('Collection:', environment.collection);
      console.log('Using ABI:', environment.collection === 'cryptophunks' ? 'FlashLoanContractPhunkABI' : 'FlashLoanContractABI');
      
      console.log('=== Executing transaction ===');
      const result = await walletClient.writeContract(tx);
      console.log('Transaction successful! Hash:', result);
      console.log('=== cancelBidWithFlashLoan SUCCESS ===');
      return result;
      
    } catch (error: any) {
      console.error('=== cancelBidWithFlashLoan ERROR ===');
      console.error('Error details:', error);
      console.error('Error message:', error?.message);
      console.error('Error code:', error?.code);
      console.error('Error data:', error?.data);
      console.error('Stack trace:', error?.stack);
      throw error;
    }
  }

  async decodeInputData(data: string): Promise<any> {
    const decoded = decodeFunctionData({
      abi: CryptoPunksMarketABI,
      data: data as `0x${string}`,
    });
    return decoded;
  }

  async decodeStashTransactionData(data: string): Promise<any> {
    const decoded = decodeFunctionData({
      abi: StashABI,
      data: data as `0x${string}`,
    });
    return decoded;
  }

  async waitForTransaction(hash: string): Promise<TransactionReceipt> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) throw new Error('No public client');
    const transaction = await publicClient.waitForTransactionReceipt({ hash: hash as `0x${string}` });
    return transaction;
  }

  async getCurrentBlockNumber(): Promise<number> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) {
      throw new Error('No public client');
    }
    return Number(await publicClient.getBlockNumber());
  }

  async prepareV1PunkWrapping(tokenId: string): Promise<string | undefined> {

    const network = await this.checkNetwork();
    const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
    const functionName = 'offerPunkForSaleToAddress'

    const tx: any = {
      address: environment.v1PunksMarketAddress,
      abi: environment.collection === 'cryptophunks' ? CryptoPhunksMarketABI : CryptoPunksMarketABI,
      functionName,
      args: [tokenId, 0, environment.wrappedPunksAddress]
    };

    return await walletClient?.writeContract(tx);
  }

  async wrapV1Punk(tokenId: string): Promise<string | undefined> {

    const network = await this.checkNetwork();
    const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
    const functionName = 'wrap'
    const tx: any = {
      address: environment.wrappedPunksAddress,
      abi: PunkV1Wrapper,
      functionName,
      args: [tokenId]
    };
    tx.value = 0;

    return await walletClient?.writeContract(tx);
  }

  async isUnwrapV1PunksApproved(): Promise<boolean> {
    if (environment.collection !== 'cryptopunks-v1' || !environment.unwrapV1PunksAddress) {
      return false;
    }

    const publicClient = getPublicClient(this.config);
    const walletAddress = this.stateSvc.getWalletAddress();
    if (!publicClient || !walletAddress) {
      return false;
    }

    try {
      const isApprovedForAll = await publicClient.readContract({
        address: environment.wrappedPunksAddress as `0x${string}`,
        abi: ERC721ABI,
        functionName: 'isApprovedForAll',
        args: [walletAddress, environment.unwrapV1PunksAddress as `0x${string}`],
      });
      return Boolean(isApprovedForAll);
    } catch (error) {
      console.error('[Web3Service] Error checking UnwrapV1Punks approval:', error);
      return false;
    }
  }

  async approveUnwrapV1Punks(): Promise<string | undefined> {
    if (environment.collection !== 'cryptopunks-v1' || !environment.unwrapV1PunksAddress) {
      return;
    }

    const network = await this.checkNetwork();
    const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
    if (!walletClient) {
      return;
    }

    return walletClient.writeContract({
      address: environment.wrappedPunksAddress as `0x${string}`,
      abi: ERC721ABI,
      functionName: 'setApprovalForAll',
      args: [environment.unwrapV1PunksAddress as `0x${string}`, true],
    });
  }

  async unwrapAllV1Punks(punkIds: number[]): Promise<string | undefined> {
    if (environment.collection !== 'cryptopunks-v1' || !environment.unwrapV1PunksAddress || punkIds.length === 0) {
      return;
    }

    const network = await this.checkNetwork();
    const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
    if (!walletClient) {
      return;
    }

    return walletClient.writeContract({
      address: environment.unwrapV1PunksAddress as `0x${string}`,
      abi: UnwrapV1PunksABI,
      functionName: 'unwrap',
      args: [punkIds.map((id) => Number(id))],
    });
  }

  async offerPunkForSale(
    tokenId: string, 
    value: number, 
    toAddress?: string | null,
    marketContractAddress?: string
  ): Promise<string | undefined> {
    console.log('offerPunkForSale', { tokenId, value, toAddress, marketContractAddress });
    const weiValue = this.ethToWei(value);

    if (toAddress) {
      if (!isAddress(toAddress)) throw new Error('Invalid address');
      return this.punkContractInteraction(
          environment.collection === 'cryptophunks' ? 'offerPhunkForSaleToAddress' 
            : environment.collection === 'cryptopunks-v1' ? 'offerPunkForSaleToAddress'
            : 'offerPunkForSaleToAddress',
        [tokenId, weiValue, toAddress],
        undefined,
        marketContractAddress
      );
    }

    const functionName = environment.collection === 'cryptophunks' ? 'offerPhunkForSale' : 'offerPunkForSale';
    return this.punkContractInteraction(
        functionName,
      [tokenId, weiValue],
      undefined,
      marketContractAddress
    );
  }

  async punkNoLongerForSale(tokenId: string): Promise<string | undefined> {
    return this.punkContractInteraction(environment.collection === 'cryptophunks' ? 'phunkNoLongerForSale' : 'punkNoLongerForSale', [tokenId]);
  }

  async transferPunk(tokenId: string, toAddress: string, wrapped:boolean=false): Promise<string | undefined> {
    if (!isAddress(toAddress)) return;

    if (environment.collection === 'cryptopunks') {
      // TODO had wrapper check here.
      // If the collection is 'cryptopunks' (main collection)
      // - If not wrapped: use punkContractInteraction (legacy transfer)
      // - If wrapped: use ERC721 transfer with wrappedPunksAddress
      // - If C721: use ERC721 transfer with c721Address
      if (environment.collection === 'cryptopunks') {
        if (!wrapped) {
          // Not wrapped: legacy transfer
          return this.punkContractInteraction('transferPunk', [toAddress, tokenId]);
        } else {
          // Wrapped: check if C721 or legacy wrapper
          // If c721Address is set in environment, use it for C721 transfer
          if (environment.c721Address) {
            // C721-wrapped: use ERC721 transfer
            return this.erc721ContractInteraction(
              environment.c721Address,
              'safeTransferFrom',
              [this.stateSvc.getWalletAddress(), toAddress, tokenId]
            );
          } else {
            // Legacy wrapper: use wrappedPunksAddress
            return this.erc721ContractInteraction(
              environment.wrappedPunksAddress,
              'safeTransferFrom',
              [this.stateSvc.getWalletAddress(), toAddress, tokenId]
            );
          }
        }
      }
      return this.punkContractInteraction('transferPunk', [toAddress, tokenId]);
    } else if (environment.collection === 'cryptopunks-v1') {
      // Indexer wrap status can lag after unwrap; WPV1 exists() is the source of truth for routing.
      const wrappedOnChain = await this.wrappedPunkV1TokenExists(tokenId);
      if (wrappedOnChain) {
        return this.erc721ContractInteraction(
          environment.wrappedPunksAddress,
          'safeTransferFrom',
          [this.stateSvc.getWalletAddress(), toAddress, tokenId],
        );
      }

      const nativeMarket = environment.v1PunksMarketAddress;
      if (!nativeMarket) {
        throw new Error('V1 native market address is not configured');
      }

      return this.anyContractInteraction(
        nativeMarket,
        'transferPunk',
        [toAddress, tokenId],
        CryptoPunksMarketABI,
      );
    } else if (environment.collection === 'cryptophunks') {
        return this.erc721ContractInteraction('0xf07468ead8cf26c752c676e43c814fee9c8cf402', 'safeTransferFrom', [this.stateSvc.getWalletAddress(), toAddress, tokenId]);
    }
    return
  }

  async unwrapPunk(tokenId: string) {
    return this.wrapperContractInteraction('unwrap', [tokenId]);
  }

  async enterBidForPunk(tokenId: string, value: number): Promise<string | undefined> {
    const weiValue = this.ethToWei(value);
    const functionName = environment.collection === 'cryptophunks' ? 'enterBidForPhunk' : 'enterBidForPunk';
    return this.punkContractInteraction(functionName, [tokenId], weiValue as any);
  }

  async withdrawBidForPunk(tokenId: string): Promise<string | undefined> {
    const functionName = environment.collection === 'cryptophunks' ? 'withdrawBidForPhunk' : 'withdrawBidForPunk';
    return this.punkContractInteraction(functionName, [tokenId]);
  }

  async acceptBidForPunk(tokenId: string, minPrice: string): Promise<string | undefined> {
    // We need to make sure this get's added.
    // Once added, this loop will run through as mm knows it's on the network.
    /*
    if (environment.collection === 'cryptopunks') {
      for (let i = 0; i < 11; i++) await this.addMevBlockerNetwork();
    }
    */
    const functionName = environment.collection === 'cryptophunks' ? 'acceptBidForPhunk' : 'acceptBidForPunk';
    return this.punkContractInteraction(functionName, [tokenId, minPrice]);
  }

  /**
   * Get the stash address for a given bidder address
   */
  async getStashAddressFor(bidderAddress: string): Promise<string> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) {
      throw new Error('No public client available');
    }

    const stashAddress = await readContract(this.config, {
      address: stashFactoryAddress,
      abi: StashFactoryABI,
      functionName: 'stashAddressFor',
      args: [bidderAddress as `0x${string}`]
    }) as `0x${string}`;

    if (!stashAddress || stashAddress === '0x0000000000000000000000000000000000000000') {
      throw new Error('No stash address found for bidder');
    }

    console.log(`[Web3Service] Stash address for bidder ${bidderAddress}: ${stashAddress}`);
    return stashAddress;
  }

  /**
   * Read the stash account nonce used in signed collection bids.
   * V1 ERC721 stashes use `erc721AccountNonce` (see processERC721Bid); V2 stashes use `punkAccountNonce`.
   */
  async getCollectionBidAccountNonce(bidderAddress: string): Promise<number> {
    try {
      const stashAddress = await this.getStashAddressFor(bidderAddress);
      const functionName =
        environment.collection === 'cryptopunks-v1' ? 'erc721AccountNonce' : 'punkAccountNonce';
      const nonce = await readContract(this.config, {
        address: stashAddress as `0x${string}`,
        abi: StashABI,
        functionName
      }) as bigint;

      const numericNonce = Number(nonce);
      if (Number.isNaN(numericNonce)) {
        throw new Error('Invalid nonce returned from stash');
      }

      console.log(`[Web3Service] ${functionName} for ${bidderAddress} (${stashAddress}): ${numericNonce}`);
      return numericNonce;
    } catch (error) {
      console.error('[Web3Service] Error fetching collection bid account nonce:', error);
      throw error;
    }
  }

  /**
   * Get the total available liquidity (WETH + ETH) for a stash address
   */
  async getStashAvailableLiquidity(stashAddress: string): Promise<bigint> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) {
      throw new Error('No public client available');
    }

    try {
      const availableWei = await readContract(this.config, {
        address: stashAddress as `0x${string}`,
        abi: StashABI,
        functionName: 'availableLiquidityWETHAndETH'
      }) as bigint;

      return availableWei;
    } catch (error) {
      console.error('[Web3Service] Error fetching stash liquidity:', error);
      throw error;
    }
  }

  /**
   * Get the available ETH liquidity for a stash address
   */
  async getStashAvailableEth(stashAddress: string): Promise<bigint> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) {
      throw new Error('No public client available');
    }

    try {
      const availableEthWei = await readContract(this.config, {
        address: stashAddress as `0x${string}`,
        abi: StashABI,
        functionName: 'availableLiquidity',
        args: [ZERO_ADDRESS as `0x${string}`]
      }) as bigint;

      return availableEthWei;
    } catch (error) {
      console.error('[Web3Service] Error fetching stash ETH liquidity:', error);
      throw error;
    }
  }

  /**
   * Withdraw ETH from a stash
   */
  async withdrawEthFromStash(stashAddress: string, amountWei: bigint): Promise<string | undefined> {
    try {
      const network = await this.checkNetwork();
      const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });

      if (!walletClient) {
        throw new Error('No wallet client available');
      }

      const tx = await walletClient.writeContract({
        address: stashAddress as `0x${string}`,
        abi: StashABI,
        functionName: 'withdraw',
        args: [ZERO_ADDRESS as `0x${string}`, amountWei]
      });

      console.log(`[Web3Service] Withdraw ETH from stash transaction hash: ${tx}`);
      return tx;
    } catch (error) {
      console.error('[Web3Service] Error withdrawing ETH from stash:', error);
      throw error;
    }
  }

  /**
   * Cancel a bid on a stash contract
   */
  async cancelBidOnStash(stashAddress: string, bidNonce: number | bigint): Promise<string | undefined> {
    try {
      const network = await this.checkNetwork();
      const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });

      if (!walletClient) {
        throw new Error('No wallet client available');
      }

      const bidNonceBigInt = typeof bidNonce === 'number' ? BigInt(bidNonce) : bidNonce;

      const tx = await walletClient.writeContract({
        address: stashAddress as `0x${string}`,
        abi: StashABI,
        functionName: 'cancelPunkBid',
        args: [bidNonceBigInt]
      });

      console.log(`[Web3Service] Cancel bid transaction hash: ${tx}`);
      return tx;
    } catch (error) {
      console.error('[Web3Service] Error canceling bid on stash:', error);
      throw error;
    }
  }

  /**
   * Cancel all bids on a stash contract
   */
  async cancelAllPunkBidsOnStash(stashAddress: string): Promise<string | undefined> {
    try {
      const network = await this.checkNetwork();
      const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });

      if (!walletClient) {
        throw new Error('No wallet client available');
      }

      // For cryptopunks-v1, use cancelAllERC721Bids instead of cancelAllPunkBids
      const functionName = environment.collection === 'cryptopunks-v1' 
        ? 'cancelAllERC721Bids' 
        : 'cancelAllPunkBids';

      const tx = await walletClient.writeContract({
        address: stashAddress as `0x${string}`,
        abi: StashABI,
        functionName: functionName,
        args: []
      });

      console.log(`[Web3Service] Cancel all ${environment.collection === 'cryptopunks-v1' ? 'ERC721' : 'punk'} bids transaction hash: ${tx}`);
      return tx;
    } catch (error) {
      console.error('[Web3Service] Error canceling all punk bids on stash:', error);
      throw error;
    }
  }

  /**
   * After cancel-all on stash is confirmed on-chain: mark all pending indexer bids for the JWT wallet
   * as invalid (PUT .../confidentials/invalidate-all marks every pending row, not only confidential).
   * No-op if there is no JWT or listings base URL.
   */
  async notifyIndexerInvalidateAllConfidentialBidsAfterCancelAll(): Promise<void> {
    if (!this.platformService.isClient || !this.http) {
      return;
    }
    const jwtToken = this.stateSvc.getJwtToken();
    if (!jwtToken) {
      console.warn(
        '[Web3Service] Skipping pending bids indexer invalidation: no JWT (sign in for orderbook sync)',
      );
      return;
    }
    const graphUri = environment.alternativeListingsGraphURI || '';
    const baseUrl = graphUri.replace(/\/graphql$/, '');
    if (!baseUrl) {
      return;
    }
    const url = `${baseUrl}/api/scraper/punk-bids/confidentials/invalidate-all`;
    try {
      await firstValueFrom(
        this.http.put<{ success: boolean; invalidatedCount: number }>(
          url,
          {},
          { headers: { Authorization: `Bearer ${jwtToken}` } },
        ),
      );
    } catch (err) {
      console.error('[Web3Service] pending bids invalidate-all request failed:', err);
    }
  }

  /**
   * Check if a punk is offered for sale to a specific address (stash)
   */
  async checkPunkOfferedToStash(
    tokenId: string,
    stashAddress: string,
    expectedMinValueWei?: string,
    marketContractAddress?: string
  ): Promise<boolean> {
    try {
      const publicClient = getPublicClient(this.config);
      if (!publicClient) {
        throw new Error('No public client available');
      }

      const result = await readContract(this.config, {
        address: (marketContractAddress || marketAddress) as `0x${string}`,
        abi: CryptoPunksMarketABI,
        functionName: 'punksOfferedForSale',
        args: [BigInt(tokenId)]
      });

      // viem can return tuples as arrays or objects depending on ABI format
      // Handle both cases: array [isForSale, punkIndex, seller, minValue, onlySellTo] or object
      let isForSale: boolean;
      let onlySellTo: string;
      let minValue: bigint = BigInt(0);

      if (Array.isArray(result)) {
        // Array format: [isForSale, punkIndex, seller, minValue, onlySellTo]
        isForSale = result[0] as boolean;
        minValue = result[3] as bigint;
        onlySellTo = result[4] as string;
      } else {
        // Object format: { isForSale, punkIndex, seller, minValue, onlySellTo }
        const offer = result as { isForSale: boolean; punkIndex: bigint; seller: `0x${string}`; minValue: bigint; onlySellTo: `0x${string}` };
        isForSale = offer.isForSale;
        minValue = offer.minValue;
        onlySellTo = offer.onlySellTo;
      }

      const stashMatches = onlySellTo.toLowerCase() === stashAddress.toLowerCase();
      const priceMatches = expectedMinValueWei != null
        ? minValue === BigInt(expectedMinValueWei)
        : true;

      // Check if the punk is offered for sale and if it's offered to the stash address with the expected price
      const isOfferedToStash = isForSale && stashMatches && priceMatches;

      console.log(
        `[Web3Service] Punk ${tokenId} offered to stash ${stashAddress}: ${isOfferedToStash}`,
        {
          isForSale,
          onlySellTo,
          stashAddress,
          minValue: minValue?.toString(),
          expectedMinValueWei
        }
      );
      return isOfferedToStash;
    } catch (error) {
      console.error('[Web3Service] Error checking if punk is offered to stash:', error);
      return false;
    }
  }

  /**
   * Check if a stash is deployed for a given owner address
   */
  async checkStashDeployed(ownerAddress: string): Promise<boolean> {
    try {
      const publicClient = getPublicClient(this.config);
      if (!publicClient) {
        throw new Error('No public client available');
      }

      const deployed = await readContract(this.config, {
        address: stashFactoryAddress,
        abi: StashFactoryABI,
        functionName: 'ownerHasDeployed',
        args: [ownerAddress as `0x${string}`]
      }) as boolean;

      return deployed;
    } catch (error) {
      console.error('[Web3Service] Error checking stash deployment:', error);
      throw error;
    }
  }

  /**
   * Deploy a stash for a given owner address
   */
  async deployStash(ownerAddress: string): Promise<string | undefined> {
    try {
      const network = await this.checkNetwork();
      const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
      
      if (!walletClient) {
        throw new Error('No wallet client available');
      }

      const tx = await walletClient.writeContract({
        address: stashFactoryAddress,
        abi: StashFactoryABI,
        functionName: 'deployStash',
        args: [ownerAddress as `0x${string}`]
      });

      console.log(`[Web3Service] Deploy stash transaction hash: ${tx}`);
      return tx;
    } catch (error) {
      console.error('[Web3Service] Error deploying stash:', error);
      throw error;
    }
  }

  /**
   * Read ERC721StashFactory.currentVersion() (latest registered implementation index).
   */
  async getStashFactoryCurrentVersion(): Promise<bigint | null> {
    try {
      const publicClient = getPublicClient(this.config);
      if (!publicClient) {
        return null;
      }
      const v = await readContract(this.config, {
        address: stashFactoryAddress,
        abi: StashFactoryABI,
        functionName: 'currentVersion',
        args: []
      }) as bigint;
      return v;
    } catch (error) {
      console.warn('[Web3Service] currentVersion not available on factory:', error);
      return null;
    }
  }

  /**
   * Read on-chain implementation version from a deployed stash proxy.
   */
  async getDeployedStashVersion(stashAddress: string): Promise<bigint | null> {
    try {
      const v = await readContract(this.config, {
        address: stashAddress as `0x${string}`,
        abi: StashABI,
        functionName: 'version',
        args: []
      }) as bigint;
      return v;
    } catch (error) {
      console.warn('[Web3Service] stash.version() read failed:', error);
      return null;
    }
  }

  /**
   * Whether the stash proxy should be upgraded so its reported version matches the factory's currentVersion.
   */
  async getStashUpgradeStatus(stashAddress: string): Promise<{
    needsUpgrade: boolean;
    stashVersion: bigint;
    factoryCurrentVersion: bigint;
  }> {
    const factoryV = (await this.getStashFactoryCurrentVersion()) ?? 0n;
    const stashV = (await this.getDeployedStashVersion(stashAddress)) ?? 0n;
    const needsUpgrade =
      factoryV > 0n && stashV > 0n && factoryV !== stashV;
    return { needsUpgrade, stashVersion: stashV, factoryCurrentVersion: factoryV };
  }

  /**
   * Upgrade the caller's deterministic stash proxy to the factory's latest implementation.
   * Caller must be the stash owner (same address used for deployStash).
   */
  async upgradeStash(): Promise<string | undefined> {
    try {
      const network = await this.checkNetwork();
      const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });

      if (!walletClient) {
        throw new Error('No wallet client available');
      }

      const tx = await walletClient.writeContract({
        address: stashFactoryAddress,
        abi: StashFactoryABI,
        functionName: 'upgradeStash',
        args: []
      });

      console.log(`[Web3Service] upgradeStash transaction hash: ${tx}`);
      return tx;
    } catch (error) {
      console.error('[Web3Service] Error upgrading stash:', error);
      throw error;
    }
  }

  /**
   * Send ETH to an address
   */
  async sendEthToAddress(toAddress: string, amountWei: string): Promise<string | undefined> {
    try {
      const network = await this.checkNetwork();
      const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
      
      if (!walletClient) {
        throw new Error('No wallet client available');
      }

      const tx = await walletClient.sendTransaction({
        to: toAddress as `0x${string}`,
        value: BigInt(amountWei)
      });

      console.log(`[Web3Service] Send ETH transaction hash: ${tx}`);
      return tx;
    } catch (error) {
      console.error('[Web3Service] Error sending ETH:', error);
      throw error;
    }
  }

  /**
   * Sign a PunkBid or ERC721Bid struct that can be submitted to processPunkBid or processERC721Bid
   */
  async signPunkBidMessage(messageData: SignPunkBidParams): Promise<{
    signature: string;
    typedData: PunkBidTypedData | ERC721BidTypedData;
    verifyingContract: `0x${string}`;
  }> {
    try {
      const network = await this.checkNetwork();
      const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
      
      if (!walletClient) {
        throw new Error('No wallet client available');
      }

      const account = await getAccount(this.config);
      if (!account.address) {
        throw new Error('No account address available');
      }

      const verifyingContract = (messageData.verifyingContract
        ?? await this.getStashAddressFor(account.address)) as `0x${string}`;

      // For V1, use ERC721Bid typed data; for V2, use PunkBid typed data
      const isV1 = environment.collection === 'cryptopunks-v1';
      
      let signature: string;
      let typedData: PunkBidTypedData | ERC721BidTypedData;
      
      if (isV1) {
        const erc721TypedData = createERC721BidTypedData({
          chainId: network.chain?.id || 1,
          verifyingContract,
          root: messageData.root,
          accountNonce: messageData.accountNonce,
          bidNonce: messageData.bidNonce,
          expiration: messageData.expiration,
          order: messageData.order,
        });
        console.log('[Web3Service] ERC721Bid signature generated for V1');
        
        signature = await walletClient.signTypedData({
          domain: erc721TypedData.domain,
          types: erc721TypedData.types,
          primaryType: erc721TypedData.primaryType,
          message: erc721TypedData.message,
        });
        
        typedData = erc721TypedData;
      } else {
        const punkTypedData = createPunkBidTypedData({
          chainId: network.chain?.id || 1,
          verifyingContract,
          root: messageData.root,
          accountNonce: messageData.accountNonce,
          bidNonce: messageData.bidNonce,
          expiration: messageData.expiration,
          order: messageData.order,
        });
        console.log('[Web3Service] PunkBid signature generated for V2');
        
        signature = await walletClient.signTypedData({
          domain: punkTypedData.domain,
          types: punkTypedData.types,
          primaryType: punkTypedData.primaryType,
          message: punkTypedData.message,
        });
        
        typedData = punkTypedData;
      }

      return {
        signature,
        typedData,
        verifyingContract,
      };
    } catch (error) {
      console.error('[Web3Service] Error signing punk bid message:', error);
      throw error;
    }
  }

  async acceptCollectionBidForPunk(
    tokenId: string,
    bidAmountWei: string,
    bidderAddress: string,
    merkleRoot: string,
    accountNonce: number,
    bidNonce: number | string,
    expiration: number | string,
    signature: string,
    proofs: string[]
  ): Promise<string | undefined> {
    try {
      // Step 1: Get the stash address for the bidder
      const stashAddress = await this.getStashAddressFor(bidderAddress);

      const network = await this.checkNetwork();
      const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
      const publicClient = getPublicClient(this.config);
      
      if (!walletClient) {
        throw new Error('No wallet client available');
      }
      if (!publicClient) {
        throw new Error('No public client available');
      }

      // Convert parameters to the correct types
      const bidAmount = BigInt(bidAmountWei);
      const merkleRootBytes = merkleRoot as `0x${string}`;
      const accountNonceBigInt = BigInt(accountNonce);
      const bidNonceBigInt = BigInt(bidNonce);
      const expirationBigInt = BigInt(expiration);
      
      // Convert signature to bytes
      const signatureBytes = signature as `0x${string}`;
      
      // Convert proof array to bytes32[]
      const proofBytes32 = proofs.map(p => p as `0x${string}`) as `0x${string}`[];

      const account = await getAccount(this.config);
      const simulationAccount = account.address as `0x${string}` | undefined;

      // Check if this is V1 collection - use processERC721Bid flow
      if (environment.collection === 'cryptopunks-v1') {
        // ERC721 approve only when WPV1 has minted this id; native V1 uses offer+buy at 0 (no approve)
        const wrappedErc721 = await this.wrappedPunkV1TokenExists(tokenId);
        if (wrappedErc721) {
          const isApproved = await this.checkStashApproval(tokenId, stashAddress);

          if (!isApproved) {
            const error: any = new Error('STASH_APPROVAL_REQUIRED');
            error.code = 'STASH_APPROVAL_REQUIRED';
            throw error;
          }
        } else {
          // Native V1 settlement requires stash implementation v2+ (transferPunk on OG; v1 impl auto-wrapped).
          const stashVer = await this.getStashContractVersion(stashAddress);
          const minNativeV1StashVersion = 2n;
          if (stashVer < minNativeV1StashVersion) {
            throw new Error(
              'This bid uses a stash that is still on implementation v1: it will auto-wrap native punks. ' +
                'The bidder must upgrade their stash (v2+) first — e.g. Upgrade stash in Create collection bid, or call upgradeStash() on the factory from their wallet. ' +
                `Stash ${stashAddress} reports version ${stashVer.toString()}.`
            );
          }
        }

        // Step 3: Call processERC721Bid on the stash address for V1
        // Function signature: processERC721Bid(ERC721Bid calldata bid, uint256 tokenId, bytes memory signature, bytes32[] calldata proof)
        // Struct: ERC721Bid { Order order, uint256 accountNonce, uint256 bidNonce, uint256 expiration, bytes32 root }
        // Struct: Order { uint16 numberOfUnits, uint80 pricePerUnit, address auction }
        // For V1, auction must be wrappedPunksAddress (as per contract line 297)
        const erc721Bid = {
          order: {
            numberOfUnits: 1, // Single token bid
            pricePerUnit: bidAmount, // bidAmount fits in uint80
            auction: environment.wrappedPunksAddress as `0x${string}` // Must match _WRAPPED_PUNKS_V1
          },
          accountNonce: accountNonceBigInt,
          bidNonce: bidNonceBigInt,
          expiration: expirationBigInt,
          root: merkleRootBytes
        };

        const tokenIdBigInt = BigInt(tokenId);

        // Log for debugging
        console.log('[Web3Service] V1 bid acceptance:', {
          stashAddress,
          tokenId,
          erc721Bid,
          signatureLength: signatureBytes.length,
          verifyingContract: stashAddress, // Signature should be created with this as verifyingContract
        });

        const simulation = await publicClient.simulateContract({
          account: simulationAccount,
          address: stashAddress as `0x${string}`,
          abi: StashABI,
          functionName: 'processERC721Bid',
          args: [
            erc721Bid,
            tokenIdBigInt,
            signatureBytes,
            proofBytes32
          ]
        });

        const tx = await walletClient.writeContract(simulation.request);

        console.log(`[Web3Service] processERC721Bid transaction hash: ${tx}`);
        return tx;
      } else {
        // V2 (or other) - use existing processPunkBid flow
        const punkIndex = BigInt(tokenId);

        // Construct the PunkBid struct with nested Order struct
        // Function signature: processPunkBid(PunkBid calldata bid, uint256 punkIndex, bytes memory signature, bytes32[] calldata proof)
        // Struct: PunkBid { Order order, uint256 accountNonce, uint256 bidNonce, uint256 expiration, bytes32 root }
        // Struct: Order { uint16 numberOfUnits, uint80 pricePerUnit, address auction }
        const punkBid = {
          order: {
            numberOfUnits: 1, // Single punk bid
            pricePerUnit: bidAmount, // bidAmount fits in uint80
            auction: marketAddress as `0x${string}`
          },
          accountNonce: accountNonceBigInt,
          bidNonce: bidNonceBigInt,
          expiration: expirationBigInt,
          root: merkleRootBytes
        };

        const simulation = await publicClient.simulateContract({
          account: simulationAccount,
          address: stashAddress as `0x${string}`,
          abi: StashABI,
          functionName: 'processPunkBid',
          args: [
            punkBid,
            punkIndex,
            signatureBytes,
            proofBytes32
          ]
        });

        const tx = await walletClient.writeContract(simulation.request);

        console.log(`[Web3Service] processPunkBid transaction hash: ${tx}`);
        return tx;
      }
    } catch (error) {
      console.error('[Web3Service] Error accepting collection bid:', error);
      throw error;
    }
  }

  async buyPunk(tokenId: string, value: string): Promise<string | undefined> {
    const functionName = environment.collection === 'cryptophunks' ? 'buyPhunk' : 'buyPunk';
    return this.punkContractInteraction(functionName, [tokenId], value as any);
  }

  async withdraw(): Promise<any> {
    const hash = await this.punkContractInteraction('withdraw', []);
    const receipt = await this.waitForTransaction(hash!);
    return await this.checkHasWithdrawal(receipt.from);
  }

  async getBid(tokenId: string): Promise<any> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) throw new Error('No public client');
    const res: any = await publicClient.readContract({
      address: marketAddress as `0x${string}`,
      abi: CryptoPunksMarketABI,
      functionName: 'punkBids',
      args: [tokenId],
    });
    return res[3];
  }

  async getPunkOwner(tokenId: string): Promise<any> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) throw new Error('No public client');
    const res: any = await publicClient.readContract({
      address: marketAddress as `0x${string}`,
      abi: CryptoPunksMarketABI,
      functionName: 'punkIndexToAddress',
      args: [tokenId],
    });
    return res;
  }

  //////////////////////////////////
  // TXNS //////////////////////////
  //////////////////////////////////

  async getTransaction(hash: string): Promise<any> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) throw new Error('No public client');
    const transaction = await publicClient.getTransaction({ hash: hash as `0x${string}` });
    return transaction;
  }

  async getTransactionReceipt(hash: string): Promise<any> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) throw new Error('No public client');
    const transaction = await publicClient.getTransactionReceipt({ hash: hash as `0x${string}` });
    return transaction;
  }

  // ///////////////////////////////
  // NETWORKS //////////////////////
  //////////////////////////////////

  async addMevBlockerNetwork(): Promise<void> {

    const mevBlockerRPC: Chain = {
      id: environment.chainId,
      name: 'MEV Blocker (Ethereum Mainnet)',
      nativeCurrency: {
        decimals: 18,
        name: 'Ether',
        symbol: 'ETH',
      },
      rpcUrls: {
        public: { http: ['https://rpc.mevblocker.io'] },
        default: { http: ['https://rpc.mevblocker.io'] },
      },
      blockExplorers: {
        etherscan: { name: 'Etherscan', url: 'https://etherscan.io' },
        default: { name: 'Etherscan', url: 'https://etherscan.io' },
      },
    };

    const walletClient = await getWalletClient(this.config, { chainId: environment.chainId });
    return walletClient.addChain({ chain: mevBlockerRPC });
  }

  //////////////////////////////////
  // UTILS /////////////////////////
  //////////////////////////////////

  /**
   * Authenticate with Web3 signature and get JWT token
   */
  async authenticateWithWeb3(address: string): Promise<void> {
    if (this.platformService.isServer || !this.authService) {
      return;
    }

    if (this.authInFlight) {
      return;
    }

    this.authInFlight = true;

    try {
      const requestedIdentity = address.toLowerCase();
      const authenticatedAs = this.stateSvc.getAuthenticatedAsAddress()?.toLowerCase();

      // Skip only when a valid token already represents the same identity.
      if (this.stateSvc.isTokenValid() && authenticatedAs === requestedIdentity) {
        console.log('[Web3Service] JWT token already valid for requested identity, skipping authentication');
        return;
      }

      // Get auth message from backend
      const message = await this.authService.getAuthMessage();
      if (!message) {
        throw new Error('Failed to get auth message');
      }

      // Get wallet client for signing - always sign with connected account (delegate), not the identity we claim
      const walletClient = await getWalletClient(this.config);
      if (!walletClient) {
        throw new Error('No wallet client available');
      }
      const connectedAccount = getAccount(this.config).address ?? this.stateSvc.getWalletAddress();
      if (!connectedAccount) {
        throw new Error('No connected account available');
      }

      // Sign the message with the connected wallet; backend receives (address = identity, signature from connected)
      const signature = await walletClient.signMessage({
        message,
        account: connectedAccount as `0x${string}`
      });

      // Exchange signature for JWT token
      const authResponse = await this.authService.signin(requestedIdentity, signature);

      // Store token in state service
      this.stateSvc.setJwtToken(authResponse.accessToken, authResponse.expiresIn, authResponse.address);

      console.log('[Web3Service] Successfully authenticated and stored JWT token');
    } catch (error: any) {
      // Handle user rejection gracefully
      if (error?.code === 4001 || error?.message?.includes('rejected') || error?.message?.includes('denied')) {
        console.log('[Web3Service] User rejected signature request');
        return;
      }
      
      // Log other errors but don't throw - authentication is optional
      console.error('[Web3Service] Error during Web3 authentication:', error);
      throw error;
    } finally {
      this.authInFlight = false;
    }
  }

  //////////////////////////////////
  // WALLET MIMIC //////////////////
  //////////////////////////////////

  isAdminWallet(address?: string | null): boolean {
    const addr = (address ?? this.stateSvc.getRealWalletAddress())?.toLowerCase();
    if (!addr) return false;
    return (environment.adminWallets ?? []).some(a => a.toLowerCase() === addr);
  }

  /** True on localhost when the active dev environment enables enableLocalMimic. */
  isLocalDevMimicAllowed(): boolean {
    if (this.platformService.isServer) {
      return false;
    }
    return isLocalDevMimicEnabled();
  }

  /** Keep mimic when wagmi disconnects during localhost dev / MCP (do not tie to synthetic flag). */
  private shouldPreserveLocalMimicSession(): boolean {
    return this.isLocalDevMimicAllowed() && this.stateSvc.isMimicking();
  }

  async activateMimic(targetAddress: string): Promise<void> {
    if (this.platformService.isServer) return;

    const realAddress = this.stateSvc.getRealWalletAddress();
    const localMimicWithoutAdmin = this.isLocalDevMimicAllowed();
    if (!localMimicWithoutAdmin && !this.isAdminWallet(realAddress)) {
      console.warn('[Web3Service] Mimic rejected: connected wallet is not an admin');
      return;
    }

    if (!isAddress(targetAddress)) {
      console.warn('[Web3Service] Mimic rejected: invalid address', targetAddress);
      return;
    }

    const normalized = targetAddress.toLowerCase();
    console.log(
      `[Web3Service] Activating mimic as ${normalized}` +
        (localMimicWithoutAdmin ? ' (localhost, no admin wallet required)' : ''),
    );

    // Keep the admin JWT as-is: backend resolves the effective address via
    // X-Mimic-As header (sent by buildAuthHeaders) when the caller is an admin.
    // No need to obtain a separate JWT for the mimicked address.
    const currentToken = this.stateSvc.getJwtToken();
    const currentExpiry = this.stateSvc.getJwtTokenExpiry();
    const currentAuthAs = this.stateSvc.getAuthenticatedAsAddress();
    if (currentToken && currentExpiry) {
      this.savedJwtBeforeMimic = { token: currentToken, expiry: currentExpiry, address: currentAuthAs };
    }

    this.stateSvc.setMimicAddress(normalized);

    if (localMimicWithoutAdmin && !this.stateSvc.getRealWalletAddress()) {
      this.localMimicSyntheticConnection = true;
    }
    if (localMimicWithoutAdmin) {
      this.stateSvc.setWeb3Connected(true);
    }

    this.notifyMimicStateChanged();
  }

  deactivateMimic(): void {
    if (!this.stateSvc.isMimicking()) return;

    console.log('[Web3Service] Deactivating mimic');
    this.stateSvc.clearMimicAddress();

    if (this.localMimicSyntheticConnection && !getAccount(this.config)?.address) {
      this.stateSvc.setWeb3Connected(false);
    }
    this.localMimicSyntheticConnection = false;

    // Restore the original JWT or clear it
    if (this.savedJwtBeforeMimic) {
      const { token, expiry, address } = this.savedJwtBeforeMimic;
      const remainingSeconds = Math.max(0, Math.floor((expiry - Date.now()) / 1000));
      if (remainingSeconds > 0) {
        this.stateSvc.setJwtToken(token, remainingSeconds, address ?? undefined);
      } else {
        this.stateSvc.clearJwtToken();
      }
      this.savedJwtBeforeMimic = null;
    } else {
      this.stateSvc.clearJwtToken();
    }

    // Emit once mimic state + JWT restoration are settled.
    this.notifyMimicStateChanged();
  }

  private notifyMimicStateChanged(): void {
    this.mimicStateChangedSubject.next(this.mimicStateChangedSubject.getValue() + 1);
  }

  async getCurrentBlock(): Promise<number> {
    const blockNum = await getPublicClient(this.config)?.getBlockNumber();
    return Number(blockNum);
  }

  ethToWei(eth: number): bigint {
    if (!eth) return BigInt(0);
    return parseEther(`${eth}`, 'wei');
  }

  weiToEth(wei: any): string {
    return formatEther(wei);
  }

  verifyAddress(address: string | null): string | null {
    if (!address) return null;
    const valid = isAddress(address);
    if (valid) return address.toLowerCase();
    return null;
  }

  async verifyAddressOrEns(address: string | null): Promise<string | null> {
    try {
      if (!address) throw new Error('No address provided');

      address = address.toLowerCase();
      const isEns = address?.includes('.eth');
      const isAddress = this.verifyAddress(address);

      if (!isEns && !isAddress) throw new Error('Invalid Address');

      if (isEns) address = await this.getEnsOwner(address);
      else address = this.verifyAddress(address);

      if (!address) throw new Error('Invalid Address');
      return address;
    } catch (error) {
      console.log(error)
      return null;
    }
  }

  async getEnsOwner(name: string) {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) return null;
    return await publicClient.getEnsAddress({ name });
  }

  async getEnsFromAddress(address: string | null | undefined): Promise<string | null> {
    if (!address) return null;
    try {
      const ens = await getPublicClient(this.config)?.getEnsName({ address: address as `0x${string}` });
      if (!ens) throw new Error('No ENS found');
      return ens;
    } catch (err) {
      return null;
    }
  }

  async getPunkImage(tokenId: string): Promise<string | null> {
    // console.log({tokenId})
    const data = await readContract(this.config, {
      address: dataAddress as `0x${string}`,
      abi: punkDataABI,
      functionName: 'punkImage',
      args: [tokenId],
    });
    // console.log(data);
    return data as string;
  }

  async getPunkAttributes(tokenId: string): Promise<string | null> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) return null;
    try {
      const res: any = await publicClient.readContract({
        address: dataAddress as `0x${string}`,
        abi: punkDataABI,
        functionName: 'punkAttributes',
        args: [tokenId],
      });
      return res;
    } catch (error) {
      console.error('Error getting punk attributes:', error);
      return null;
    }
  }

  // Methods to handle ERC721 approvals
  async checkApproval(): Promise<boolean> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) return false;

    const walletAddress = this.stateSvc.getWalletAddress();
    if (!walletAddress) return false;

    try {
      // For collections that require ERC721 approval
      if (environment.collection === 'cryptophunks' || environment.collection === 'cryptopunks-v1') {
        const isApprovedForAll = await publicClient.readContract({
          address: environment.wrappedPunksAddress as `0x${string}`,
          abi: ERC721ABI,
          functionName: 'isApprovedForAll',
          args: [walletAddress, marketAddress],
        });
        return isApprovedForAll as boolean;
      }
      
      // For original CryptoPunks, no approval needed
      return true;
    } catch (error) {
      console.error('Error checking approval:', error);
      return false;
    }
  }

  /**
   * Check if the seller has approved the marketplace for a specific token
   */
  async checkSellerApproval(tokenId: string, sellerAddress: string): Promise<boolean> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) return false;

    try {
      // For collections that require ERC721 approval
      if (environment.collection === 'cryptophunks' || environment.collection === 'cryptopunks-v1') {
        const isApprovedForAll = await publicClient.readContract({
          address: environment.wrappedPunksAddress as `0x${string}`,
          abi: ERC721ABI,
          functionName: 'isApprovedForAll',
          args: [sellerAddress, marketAddress],
        });
        return isApprovedForAll as boolean;
      }
      
      // For original CryptoPunks, no approval needed
      return true;
    } catch (error) {
      console.error('Error checking seller approval:', error);
      return false;
    }
  }

  /**
   * True if this punk id is minted as an ERC721 on Wrapped Punks V1 (WPV1).
   * Native-only punks are not ERC721; they never need approve on WPV1.
   */
  async wrappedPunkV1TokenExists(tokenId: string): Promise<boolean> {
    if (environment.collection !== 'cryptopunks-v1') {
      return false;
    }
    const exists = await readContract(this.config, {
      address: environment.wrappedPunksAddress as `0x${string}`,
      abi: PunkV1Wrapper,
      functionName: 'exists',
      args: [BigInt(tokenId)]
    });
    return Boolean(exists);
  }

  /** On-chain `ERC721Stash.version()` (implementation logic revision). */
  async getStashContractVersion(stashAddress: string): Promise<bigint> {
    return readContract(this.config, {
      address: stashAddress as `0x${string}`,
      abi: StashABI,
      functionName: 'version',
      args: []
    }) as Promise<bigint>;
  }

  /**
   * Check if the stash contract is approved to transfer the token
   * @param tokenId The token ID to check
   * @param stashAddress The stash contract address
   * @returns true if approved (either for this token or for all tokens)
   */
  async checkStashApproval(tokenId: string, stashAddress: string, c721wrapped?: boolean): Promise<boolean> {
    const publicClient = getPublicClient(this.config);
    if (!publicClient) return false;

    try {
      const account = await getAccount(this.config);
      if (!account.address) return false;

      const stashAddressTyped = stashAddress as `0x${string}`;
      const ownerAddress = account.address as `0x${string}`;

      // Determine which contract to check based on collection and wrapper type
      let contractAddress: `0x${string}`;
      if (environment.collection === 'cryptopunks') {
        // For V2, check if it's C721 or legacy wrapper
        if (c721wrapped && environment.c721Address) {
          contractAddress = environment.c721Address as `0x${string}`;
        } else {
          contractAddress = environment.wrappedPunksAddress as `0x${string}`;
        }
      } else {
        // For V1, WPV1 only applies when the token is actually minted there
        if (!(await this.wrappedPunkV1TokenExists(tokenId))) {
          return true;
        }
        contractAddress = environment.wrappedPunksAddress as `0x${string}`;
      }

      // Check if approved for this specific token
      const approvedAddress = await publicClient.readContract({
        address: contractAddress,
        abi: ERC721ABI,
        functionName: 'getApproved',
        args: [BigInt(tokenId)]
      }) as `0x${string}` | null;

      if (approvedAddress && approvedAddress.toLowerCase() === stashAddressTyped.toLowerCase()) {
        return true;
      }

      // Check if approved for all tokens
      const isApprovedForAll = await publicClient.readContract({
        address: contractAddress,
        abi: ERC721ABI,
        functionName: 'isApprovedForAll',
        args: [ownerAddress, stashAddressTyped]
      });

      return isApprovedForAll as boolean;
    } catch (error) {
      console.error('Error checking stash approval:', error);
      return false;
    }
  }

  /**
   * Approve the stash contract to transfer a specific token
   * @param tokenId The token ID to approve
   * @param stashAddress The stash contract address
   * @param c721wrapped Optional: true if token is C721 wrapped, false for legacy wrapper
   * @returns Transaction hash
   */
  async approveTokenForStash(tokenId: string, stashAddress: string, c721wrapped?: boolean): Promise<string | undefined> {
    const network = await this.checkNetwork();
    const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
    const publicClient = getPublicClient(this.config);

    if (!walletClient || !publicClient) {
      throw new Error('No wallet client available');
    }

    // Determine which contract to approve based on collection and wrapper type
    let contractAddress: `0x${string}`;
    if (environment.collection === 'cryptopunks') {
      // For V2, check if it's C721 or legacy wrapper
      if (c721wrapped && environment.c721Address) {
        contractAddress = environment.c721Address as `0x${string}`;
      } else {
        contractAddress = environment.wrappedPunksAddress as `0x${string}`;
      }
    } else {
      if (!(await this.wrappedPunkV1TokenExists(tokenId))) {
        throw new Error(
          'This punk is not wrapped as WPV1 (no ERC721 token). Approval does not apply — list it for 0 ETH to the stash on the CryptoPunks V1 contract instead.'
        );
      }
      contractAddress = environment.wrappedPunksAddress as `0x${string}`;
    }

    const stashAddressTyped = stashAddress as `0x${string}`;

    const account = await getAccount(this.config);
    const simulationAccount = account.address as `0x${string}` | undefined;

    // Simulate the approval transaction
    const simulation = await publicClient.simulateContract({
      account: simulationAccount,
      address: contractAddress,
      abi: ERC721ABI,
      functionName: 'approve',
      args: [stashAddressTyped, BigInt(tokenId)]
    });

    const tx = await walletClient.writeContract(simulation.request);
    console.log(`[Web3Service] Token approval transaction hash: ${tx}`);
    return tx;
  }

  /**
   * Approve the stash contract for all tokens (alternative to per-token approval)
   * @param stashAddress The stash contract address
   * @returns Transaction hash
   */
  async approveAllTokensForStash(stashAddress: string): Promise<string | undefined> {
    const network = await this.checkNetwork();
    const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
    const publicClient = getPublicClient(this.config);

    if (!walletClient || !publicClient) {
      throw new Error('No wallet client available');
    }

    const wrappedPunksAddress = environment.wrappedPunksAddress as `0x${string}`;
    const stashAddressTyped = stashAddress as `0x${string}`;

    const account = await getAccount(this.config);
    const simulationAccount = account.address as `0x${string}` | undefined;

    const simulation = await publicClient.simulateContract({
      account: simulationAccount,
      address: wrappedPunksAddress,
      abi: ERC721ABI,
      functionName: 'setApprovalForAll',
      args: [stashAddressTyped, true]
    });

    const tx = await walletClient.writeContract(simulation.request);
    console.log(`[Web3Service] setApprovalForAll transaction hash: ${tx}`);
    return tx;
  }

  /**
   * Check approvals for user's listed tokens
   */
  async checkListedTokensApprovals(): Promise<void> {
    try {
      console.log('[Web3Service] Checking approvals for listed tokens...');
      
      // Use data service to get owned tokens
      if (!this.dataSvc) {
        console.log('[Web3Service] DataService not available');
        return;
      }

      // Get owned tokens with their listings
      const ownedTokens = this.dataSvc.getOwned();
      console.log('[Web3Service] Owned tokens retrieved:', ownedTokens.length);
      
      // Filter tokens that are listed
      const listedTokens = ownedTokens.filter(token => token.listing);
      console.log('[Web3Service] Listed tokens found:', listedTokens.length);
      
      if (listedTokens.length === 0) {
        console.log('[Web3Service] No listed tokens found');
        return;
      }

      // Check approvals for each listed token
      if (listedTokens.length > 0) {
        const isApproved = await this.checkApproval();
        console.log(`[Web3Service] Tokens listed, approved: ${isApproved}`);
        
        if (!isApproved) {
          console.log(`[Web3Service] Tokens require approval`);
          this.showApprovalModal();
        }
      }
      
      console.log('[Web3Service] All listed tokens are already approved');
      
    } catch (error) {
      console.error('[Web3Service] Error checking approvals:', error);
    }
  }

  /**
   * Show approval confirmation modal
   */
  showApprovalModal(): void {
    this.approvalModalActiveSubject.next(true);
  }

  /**
   * Hide approval confirmation modal
   */
  hideApprovalModal(): void {
    this.approvalModalActiveSubject.next(false);
  }

  /**
   * Handle approval confirmation
   */
  async confirmApproval(): Promise<void> {
    try {
      this.hideApprovalModal();
      const approvalHash = await this.approveToken();
      if (approvalHash) {
        console.log('[Web3Service] Approval transaction sent:', approvalHash);
      }
    } catch (error) {
      console.error('[Web3Service] Error during approval:', error);
    }
  }

  /**
   * Show signature explanation modal
   */
  showSignatureExplanationModal(): void {
    this.signatureExplanationModalActiveSubject.next(true);
  }

  /**
   * Hide signature explanation modal
   */
  hideSignatureExplanationModal(): void {
    this.signatureExplanationModalActiveSubject.next(false);
    this.pendingAuthAddress = null;
  }

  /**
   * Handle signature explanation acceptance - proceed with authentication
   * @param identityAddress Optional: address to sign in as (self or delegator). If omitted, uses pendingAuthAddress (connected wallet).
   */
  async acceptSignatureExplanation(identityAddress?: string): Promise<void> {
    const address = identityAddress ?? this.pendingAuthAddress;
    if (!address) {
      this.hideSignatureExplanationModal();
      return;
    }
    this.hideSignatureExplanationModal();
    // Trigger Web3 authentication for JWT token (always signs with connected wallet, sends chosen identity)
    this.authenticateWithWeb3(address).catch((error) => {
      // Silently handle authentication errors - user can still use the app
      console.log('[Web3Service] Authentication skipped or failed:', error);
    });
  }

  /**
   * Handle signature explanation decline - skip authentication
   */
  declineSignatureExplanation(): void {
    this.hideSignatureExplanationModal();
    // User declined, authentication is optional so we just continue without it
    console.log('[Web3Service] User declined signature, continuing without personalized experience');
  }

  async approveToken(): Promise<string | undefined> {
    const walletAddress = this.stateSvc.getWalletAddress();
    if (!walletAddress) return;

    try {
      if (environment.collection === 'cryptophunks' || environment.collection === 'cryptopunks-v1') {
        const network = await this.checkNetwork();
        const walletClient = await getWalletClient(this.config, { chainId: network.chain?.id });
        const publicClient = getPublicClient(this.config);

        if (!walletClient || !publicClient) {
          throw new Error('No wallet client available');
        }

        const account = await getAccount(this.config);
        const simulationAccount = (account.address ?? walletAddress) as `0x${string}`;

        const simulation = await publicClient.simulateContract({
          account: simulationAccount,
          address: environment.wrappedPunksAddress as `0x${string}`,
          abi: ERC721ABI,
          functionName: 'setApprovalForAll',
          args: [marketAddress as `0x${string}`, true]
        });

        return walletClient.writeContract(simulation.request);
      }
      
      // For original CryptoPunks, no approval needed
      return;
    } catch (error) {
      console.error('Error approving token:', error);
      throw error;
    }
  }

  async ensureApprovalAndList(tokenId: string, value: number, toAddress?: string | null): Promise<{ approvalHash?: string, listingHash?: string }> {
    // Check if approval is needed
    const isApproved = await this.checkApproval();
    
    if (!isApproved) {
      // Request approval first
      const approvalHash = await this.approveToken();
      
      if (approvalHash) {
        // Return approval hash so component can handle waiting
        return { approvalHash };
      }
    }

    // Now proceed with listing
    const listingHash = await this.offerPunkForSale(tokenId, value, toAddress);
    return { listingHash };
  }

  async getGasPrice(): Promise<string|null> {
    try {
      const publicClient = getPublicClient(this.config);
      if (!publicClient) return null;

      const block = await publicClient.getBlock({ blockTag: 'latest' });
      if (!block?.baseFeePerGas) return null;

      // Convert from wei to gwei and format to 2 decimal places
      const gasPriceInGwei = Number(formatUnits(block.baseFeePerGas as bigint, 9)).toFixed(2);
      this.gasPriceSubject.next(gasPriceInGwei);
      return gasPriceInGwei;
    } catch (error) {
      console.error('Error fetching gas price:', error);
      return null;
    }
  }

  private startBlockTracking(): void {
    console.log('[Web3Service] startBlockTracking called, isServer:', this.platformService.isServer);
    if (this.platformService.isServer) {
      console.log('[Web3Service] startBlockTracking skipped on server');
      return;
    }
    
    console.log('[Web3Service] startBlockTracking starting interval');
    interval(15000).subscribe(() => {
      console.log('[Web3Service] startBlockTracking interval triggered');
      this.updateCurrentBlock();
      this.updateLastIndexedBlock();
    });
  }

  private async updateCurrentBlock(): Promise<void> {
    try {
      const publicClient = getPublicClient(this.config);
      if (!publicClient) return;
      const blockNumber = await publicClient.getBlockNumber();
      this.currentBlockSubject.next(Number(blockNumber));
    } catch (error) {
      console.error('Error fetching current block:', error);
    }
  }

  private async updateLastIndexedBlock(): Promise<void> {
    try {
      console.log('[Web3Service] updateLastIndexedBlock called, isServer:', this.platformService.isServer);
      // Don't run on server
      if (this.platformService.isServer) {
        console.log('[Web3Service] updateLastIndexedBlock skipped on server');
        return;
      }

      const query = this.graphQLQueriesSvc?.getQuery('cryptopunks', 'GET_LAST_INDEXED_BLOCK');
      if (!query || !this.apollo) return;

      console.log('[Web3Service] updateLastIndexedBlock starting GraphQL query');

      interface LastBlockResponse {
        _meta: {
          block: {
            number: number;
          };
        };
      }

      const obs = this.apollo.watchQuery<LastBlockResponse>({
        query,
        fetchPolicy: 'network-only'
      }).valueChanges;
      if (!obs) return;
      const result = await firstValueFrom(obs);
      console.log('[Web3Service] updateLastIndexedBlock GraphQL query completed');
      if (result && result.data && result.data._meta && result.data._meta.block && result.data._meta.block.number) {
        this.lastIndexedBlockSubject.next(result.data._meta.block.number);
      }
    } catch (error) {
      console.error('Error fetching last indexed block:', error);
    }
  }
}